import React, {
  memo, useCallback, useEffect, useMemo, useState,
} from 'react'

import {
  Icon,
  Button,
  ChevronDown,
  MoreFilters as MoreFiltersIcon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import DatePublishedContent from 'Pods/SearchPage/Shared/DatePublishedContent/DatePublishedContent'
import LicenseContent from 'Pods/SearchPage/Shared/LicenseContent/LicenseContent'
import PriceContent from 'Pods/SearchPage/Shared/PriceContent/PriceContent'
import QualityContent from 'Pods/SearchPage/Shared/QualityContent/QualityContent'
import { clearDatePublished } from 'Pods/SearchPage/Store/actions/datePublished'
import { clearDesigners } from 'Pods/SearchPage/Store/actions/designers'
import { addFileType, removeFileType, clearFileTypes } from 'Pods/SearchPage/Store/actions/fileTypes'
import { clearLicenses } from 'Pods/SearchPage/Store/actions/licenses'
import { clearMoreFilters } from 'Pods/SearchPage/Store/actions/moreFilters'
import { clearPricing } from 'Pods/SearchPage/Store/actions/pricing'
import { clearQualityContent } from 'Pods/SearchPage/Store/actions/qualityContent'
import { CONTENT_SOURCE, DATE_PUBLISHED, LICENSE_TYPE, QUALITY_LEVEL } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import FiltersWrapper from 'Pods/Shared/FiltersWrapper/FiltersWrapper'
import I18n from 'Pods/Shared/I18n'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'
import useMediaQuery from 'Utils/useMediaQuery'

import AdvancedFiltersContent from './Components/AdvancedFiltersContent/AdvancedFiltersContent'
import MoreFiltersDrawer from './Components/MoreFiltersDrawer/MoreFiltersDrawer'

const translate = (key) => I18n.t(key, { ns: 'rails' })

const prefixIcon = <Icon notSpaced svgComponent={MoreFiltersIcon} color="dark" size="small" />

const trailIcon = (
  <Icon
    notSpaced
    svgComponent={ChevronDown}
    color="darker"
    className="more-filters__trigger-chevron"
  />
)

// Below md the bar collapses, so the filters open in a full-screen modal behind a "Filters" button.
const COMPACT_BREAKPOINTS = ['xs', 'sm']
// Mirrors $main-filters-slot-date-min (1760px) in search_page.scss: below it the Date Published
// dropdown is hidden from the bar and overflows into this popover. 1760 falls inside the xl
// bucket (1600-1919), so useBreakpoint can't express it — useMediaQuery does.
const DATE_OVERFLOW_QUERY = '(max-width: 1759.98px)'
const DEFAULT_LICENSES = [LICENSE_TYPE.ALL]
const DEFAULT_QUALITY = [QUALITY_LEVEL.ANY]

function MoreFilters() {
  const advancedFilters = useSearchPageStore(state => state.advancedFilters)
  const contentFilter = useSearchPageStore(state => state.contentFilter)
  const personalFilter = useSearchPageStore(state => state.personalFilter)
  const purchaseFilter = useSearchPageStore(state => state.purchaseFilter)
  const fileTypes = useSearchPageStore(state => state.fileTypes ?? EMPTY_ARRAY)
  const onSale = useSearchPageStore(state => state.onSale)
  const withSubscription = useSearchPageStore(state => state.withSubscription)
  const priceRanges = useSearchPageStore(state => state.priceRanges ?? EMPTY_ARRAY)
  const minPrice = useSearchPageStore(state => state.minPrice ?? '')
  const maxPrice = useSearchPageStore(state => state.maxPrice ?? '')
  const licenses = useSearchPageStore(state => state.licenses ?? DEFAULT_LICENSES)
  const aiUsageAllowed = useSearchPageStore(state => state.aiUsageAllowed ?? false)
  const datePublished = useSearchPageStore(state => state.datePublished ?? DATE_PUBLISHED.ALL_TIME)
  const quality = useSearchPageStore(state => state.quality ?? DEFAULT_QUALITY)
  const contentSource = useSearchPageStore(state => state.contentSource ?? CONTENT_SOURCE.ALL)
  const designers = useSearchPageStore(state => state.designers ?? EMPTY_ARRAY)
  const breakpoint = useBreakpoint()
  // Gate the popover/modal split until mounted so the first client render matches SSR
  // (useBreakpoint is undefined on the server), avoiding a hydration mismatch.
  const [hasMounted, setHasMounted] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  const isCompact = hasMounted && COMPACT_BREAKPOINTS.includes(breakpoint)
  // Quality/Price/License live in main-filters__slot--lg (1280+); at md (960-1279) their own
  // dropdowns are hidden, so they overflow into this popover to stay reachable.
  const showLgSlotOverflow = hasMounted && breakpoint === 'md'
  // Date Published lives in main-filters__slot--date (1760+); below it (down to md) its own
  // dropdown is hidden, so it overflows into this popover. Compact uses the drawer instead.
  const isBelowDateBreakpoint = useMediaQuery(DATE_OVERFLOW_QUERY)
  const showDateOverflow = hasMounted && !isCompact && isBelowDateBreakpoint

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false)
  }, [])

  useEffect(() => {
    if (!isCompact && isDrawerOpen) {
      setIsDrawerOpen(false)
    }
  }, [isCompact, isDrawerOpen])

  // Filters owned by the More Filters popover itself; drives the desktop trigger and its Reset.
  const hasOwnFilters = useMemo(() => {
    return (
      advancedFilters.length > 0 ||
      contentFilter != null ||
      personalFilter != null ||
      purchaseFilter != null
    )
  }, [advancedFilters, contentFilter, personalFilter, purchaseFilter])

  // On compact breakpoints the trigger opens the All Filters modal, which also hosts
  // File Format, Price and Date Published — those count toward the trigger's active state there only.
  const hasModalFilters = useMemo(() => {
    return (
      hasOwnFilters ||
      fileTypes.length > 0 ||
      onSale ||
      withSubscription ||
      priceRanges.length > 0 ||
      minPrice !== '' ||
      maxPrice !== '' ||
      licenses[0] !== LICENSE_TYPE.ALL ||
      aiUsageAllowed ||
      quality[0] !== QUALITY_LEVEL.ANY ||
      contentSource !== CONTENT_SOURCE.ALL ||
      datePublished !== DATE_PUBLISHED.ALL_TIME ||
      designers.length > 0
    )
  }, [
      hasOwnFilters,
      fileTypes,
      onSale,
      withSubscription,
      priceRanges,
      minPrice,
      maxPrice,
      licenses,
      aiUsageAllowed,
      quality,
      contentSource,
      datePublished,
      designers,
    ])

  // Filters from the lg-slot dropdowns hidden at md; they count toward the desktop
  // trigger's active state only while they overflow into this popover.
  const hasOverflowFilters = useMemo(() => {
    return (
      quality[0] !== QUALITY_LEVEL.ANY ||
      contentSource !== CONTENT_SOURCE.ALL ||
      onSale ||
      withSubscription ||
      priceRanges.length > 0 ||
      minPrice !== '' ||
      maxPrice !== '' ||
      licenses[0] !== LICENSE_TYPE.ALL ||
      aiUsageAllowed
    )
  }, [
      quality,
      contentSource,
      onSale,
      withSubscription,
      priceRanges,
      minPrice,
      maxPrice,
      licenses,
      aiUsageAllowed,
    ])

  const hasDateOverflowFilter = datePublished !== DATE_PUBLISHED.ALL_TIME

  const hasPopoverFilters = hasOwnFilters
    || (showLgSlotOverflow && hasOverflowFilters)
    || (showDateOverflow && hasDateOverflowFilter)

  const handleReset = useCallback(() => {
    clearMoreFilters()
    if (showLgSlotOverflow) {
      clearQualityContent()
      clearPricing()
      clearLicenses()
    }
    if (showDateOverflow) {
      clearDatePublished()
    }
  }, [showLgSlotOverflow, showDateOverflow])

  const handleFileTypeChange = useCallback((_, value, isOn) => {
    if (isOn) {
      addFileType(value)
    } else {
      removeFileType(value)
    }
  }, [])

  const handleModalReset = useCallback(() => {
    clearFileTypes()
    clearPricing()
    clearLicenses()
    clearQualityContent()
    clearDatePublished()
    clearMoreFilters()
    clearDesigners()
  }, [])

  const handleOpenDrawer = useCallback(() => {
    setIsDrawerOpen(true)
  }, [])

  const content = useMemo(() => (
    <>
      {showLgSlotOverflow && (
        <>
          <div className="more-filters__overflow-title cgt-overline-md">
            {translate('search_page.filters.quality.title')}
          </div>
          <QualityContent hideQualityLabel />
          <div className="cgt-divider" aria-hidden="true" />
          <div className="more-filters__overflow-title cgt-overline-md">
            {translate('search_page.filters.price.title')}
          </div>
          <PriceContent />
          <div className="cgt-divider" aria-hidden="true" />
          <div className="more-filters__overflow-title cgt-overline-md">
            {translate('search_page.filters.license.title')}
          </div>
          <LicenseContent hideGroupLabels />
          <div className="cgt-divider" aria-hidden="true" />
        </>
      )}
      {showDateOverflow && (
        <>
          <div className="more-filters__overflow-title cgt-overline-md">
            {translate('search_page.filters.date_published.title')}
          </div>
          <DatePublishedContent />
          <div className="cgt-divider" aria-hidden="true" />
        </>
      )}
      <AdvancedFiltersContent />
      <Button
        type="ghost"
        size="tiny"
        customClass="filter-wrapper__reset-button"
        onClick={handleReset}
        disabled={!hasPopoverFilters}
      >
        {translate('search_page.filters.more_filters.reset')}
      </Button>
    </>
  ), [
    handleReset,
    hasPopoverFilters,
    showLgSlotOverflow,
    showDateOverflow,
  ])

  if (isCompact) {
    return (
      <>
        <Button
          type="secondary"
          variant="with-icon"
          icon={prefixIcon}
          isActive={hasModalFilters}
          onClick={handleOpenDrawer}
          customClass="filter-wrapper__button more-filters__trigger"
          aria-label={translate('search_page.filters.more_filters.trigger_label')}
          aria-expanded={isDrawerOpen}
          trailIcon={trailIcon}
        >
          <span className="more-filters__trigger-label">
            {translate('search_page.filters.more_filters.trigger_label')}
          </span>
        </Button>
        {isDrawerOpen && (
          <MoreFiltersDrawer
            onClose={handleCloseDrawer}
            onReset={handleModalReset}
            fileTypes={fileTypes}
            onFileTypeChange={handleFileTypeChange}
          />
        )}
      </>
    )
  }

  return (
    <FiltersWrapper
      placeholder={translate('search_page.filters.more_filters.desktop_title')}
      prefixIcon={prefixIcon}
      content={content}
      hasSelectedAnyFilters={hasPopoverFilters}
      closingDropdownClassName="filter-wrapper"
      contentClassName="more-filters__popover-content"
    />
  )
}

export default memo(MoreFilters)
